// localStorage-backed store for users, products, suppliers, requests.
//
// Architectural note (offline-first PWA):
// This iPad kiosk app is designed to work without a network. localStorage is
// the fast/authoritative read-path; backend sync in `lib/sync.js` mirrors
// every mutation in the background. The following rules govern what gets
// stored and mitigate the typical XSS-on-localStorage risk:
//   - PINs are never stored in plaintext. They are hashed at rest as
//     `pinHash` via `lib/crypto.js` (SHA-256 + app-scoped salt).
//   - `cb_session_v1` holds only a user id (no tokens, no credentials).
//   - `cb_pwa_dismiss_v1` is a boolean UX preference.
//   - Products / suppliers / requests are operational data (not PII).
// Because the backend is the source of truth across devices, a device
// cleared via DevTools simply re-syncs on the next load.
import { ROLES } from "../constants";
import { deleteItem, deleteRequestItem, patchRequestItem, pushItem } from "./sync";
import { hashPin } from "./crypto";

const REQ_KEY = "cb_requests_v1";
const SESSION_KEY = "cb_session_v1";
const REMEMBERED_SESSION_KEY = "cb_remembered_session_v1";
const REMEMBERED_SESSION_DAYS = 30;
const PROD_KEY = "cb_products_v1";
const SUP_KEY = "cb_suppliers_v1";
const USER_KEY = "cb_users_v1";
const CAT_KEY = "cb_categories_v1";

const SEED_VERSION_KEY = "cb_seed_version";
const CURRENT_SEED_VERSION = "9"; // bumped: categories are CRUD-able

function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
}
function writeJSON(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

// Seed only what's missing.
// Bumping CURRENT_SEED_VERSION is NOT destructive: we never overwrite an
// existing collection in localStorage, only write defaults when the
// underlying key is absent (first install). Data migrations that need to
// run on version change (e.g. hashing PINs) live in their own idempotent
// functions like `migrateUserPins()` and are invoked from App.js after
// the backend bootstrap has synced state, so no custom user/product is
// ever lost when we ship a new version of the app.
function ensureSeeded() {
  localStorage.setItem(SEED_VERSION_KEY, CURRENT_SEED_VERSION);
}
ensureSeeded();
// ---------- Users ----------
export function loadUsers() { return readJSON(USER_KEY, []); }
export function saveUsers(list) { writeJSON(USER_KEY, list); }
export function addUser(u) { const next = [u, ...loadUsers()]; saveUsers(next); pushItem("users", u); return next; }
export function updateUser(id, patch) {
  const next = loadUsers().map((u) => (u.id === id ? { ...u, ...patch } : u));
  saveUsers(next);
  const updated = next.find((u) => u.id === id);
  if (updated) pushItem("users", updated);
  return next;
}
export function removeUser(id) { const next = loadUsers().filter((u) => u.id !== id); saveUsers(next); deleteItem("users", id); return next; }
export function getUserById(id) { return loadUsers().find((u) => u.id === id) || null; }

// Migrate any legacy plaintext `pin` field into a hashed `pinHash`.
// Runs once at app startup; idempotent after the first call.
let migrationPromise = null;
export function migrateUserPins() {
  if (migrationPromise) return migrationPromise;
  migrationPromise = (async () => {
    const list = loadUsers();
    const needsMigration = list.some((u) => u.pin && !u.pinHash);
    if (!needsMigration) return;
    const nowIso = new Date().toISOString();
    const migrated = await Promise.all(
      list.map(async (u) => {
        if (!u.pin) return u;
        const pinHash = u.pinHash || (await hashPin(u.pin));
        // eslint-disable-next-line no-unused-vars
        const { pin, ...rest } = u;
        return { ...rest, pinHash, updatedAt: nowIso };
      })
    );
    saveUsers(migrated);
    // Push each migrated user to backend so other devices get the hashed form.
    migrated.forEach((u) => pushItem("users", u));
  })();
  return migrationPromise;
}

// Resolve a typed PIN to a user by hash-compare. Async.
export async function findUserByPinHash(pin) {
  const hash = await hashPin(pin);
  return (
    loadUsers().find(
      (u) => u.active !== false && ((u.pinHash && u.pinHash === hash) || u.pin === pin)
    ) || null
  );
}

// ---------- Products ----------
export function loadProducts() { return readJSON(PROD_KEY, []); }
export function saveProducts(items) { writeJSON(PROD_KEY, items); }
export function addProduct(p) { const next = [p, ...loadProducts()]; saveProducts(next); pushItem("products", p); return next; }
export function updateProduct(id, patch) {
  const nowIso = new Date().toISOString();
  const next = loadProducts().map((p) => (p.id === id ? { ...p, ...patch, updatedAt: nowIso } : p));
  saveProducts(next);
  const updated = next.find((p) => p.id === id);
  if (updated) pushItem("products", updated);
  return next;
}
export function removeProduct(id) { const next = loadProducts().filter((p) => p.id !== id); saveProducts(next); deleteItem("products", id); return next; }
export function getProductById(id) { return loadProducts().find((p) => p.id === id) || null; }

// ---------- Suppliers ----------
export function loadSuppliers() { return readJSON(SUP_KEY, []); }
export function saveSuppliers(items) { writeJSON(SUP_KEY, items); }
export function addSupplier(s) { const next = [s, ...loadSuppliers()]; saveSuppliers(next); pushItem("suppliers", s); return next; }
export function updateSupplier(id, patch) {
  const next = loadSuppliers().map((s) => (s.id === id ? { ...s, ...patch } : s));
  saveSuppliers(next);
  const updated = next.find((s) => s.id === id);
  if (updated) pushItem("suppliers", updated);
  return next;
}
export function removeSupplier(id) { const next = loadSuppliers().filter((s) => s.id !== id); saveSuppliers(next); deleteItem("suppliers", id); return next; }

// ---------- Categories ----------
function sortCategories(list) {
  // Stable: respect explicit `order` when present, fall back to creation
  // order so brand-new categories end up at the end of the list.
  return [...list].sort((a, b) => {
    const ao = a.order ?? Number.MAX_SAFE_INTEGER;
    const bo = b.order ?? Number.MAX_SAFE_INTEGER;
    if (ao !== bo) return ao - bo;
    return (a.createdAt || "").localeCompare(b.createdAt || "");
  });
}
export function loadCategories() { return sortCategories(readJSON(CAT_KEY, [])); }
export function saveCategories(items) { writeJSON(CAT_KEY, items); }
export function addCategory(c) {
  const list = loadCategories();
  const maxOrder = list.reduce((m, x) => Math.max(m, x.order ?? 0), -1);
  const enriched = { ...c, order: c.order ?? maxOrder + 1 };
  const next = [enriched, ...list];
  saveCategories(next);
  pushItem("categories", enriched);
  return sortCategories(next);
}
export function updateCategory(id, patch) {
  const next = loadCategories().map((c) => (c.id === id ? { ...c, ...patch } : c));
  saveCategories(next);
  const updated = next.find((c) => c.id === id);
  if (updated) pushItem("categories", updated);
  return sortCategories(next);
}
export function removeCategory(id) {
  const next = loadCategories().filter((c) => c.id !== id);
  saveCategories(next);
  deleteItem("categories", id);
  return sortCategories(next);
}
// Reorder by an array of ids (top→bottom). Each category gets its `order`
// field rewritten so the layout survives reloads and syncs to backend.
export function reorderCategories(orderedIds) {
  const list = loadCategories();
  const indexById = new Map(orderedIds.map((id, i) => [id, i]));
  const nowIso = new Date().toISOString();
  const next = list.map((c) => {
    const idx = indexById.get(c.id);
    if (idx == null) return c;
    return { ...c, order: idx, updatedAt: nowIso };
  });
  saveCategories(next);
  next.forEach((c) => indexById.has(c.id) && pushItem("categories", c));
  return sortCategories(next);
}
// Cascade-rename the `category` field on all products when a category
// gets renamed, so existing products stay in sync with the new label.
export function renameProductsCategory(oldName, newName) {
  if (!oldName || !newName || oldName === newName) return loadProducts();
  const nowIso = new Date().toISOString();
  const next = loadProducts().map((p) =>
    p.category === oldName ? { ...p, category: newName, updatedAt: nowIso } : p
  );
  saveProducts(next);
  next.filter((p) => p.category === newName && p.updatedAt === nowIso).forEach((p) => pushItem("products", p));
  return next;
}

// ---------- Requests ----------
export function loadRequests() { return readJSON(REQ_KEY, []); }
export function saveRequests(list) { writeJSON(REQ_KEY, list); }
export function addRequest(r) { const next = [r, ...loadRequests()]; saveRequests(next); pushItem("requests", r); return next; }
export function updateRequest(id, patch) {
  const nowIso = new Date().toISOString();
  const next = loadRequests().map((r) => (r.id === id ? { ...r, ...patch, updatedAt: nowIso } : r));
  saveRequests(next);
  const updated = next.find((r) => r.id === id);
  if (updated) pushItem("requests", updated);
  return next;
}
export function removeRequest(id) { const next = loadRequests().filter((r) => r.id !== id); saveRequests(next); deleteItem("requests", id); return next; }

function requestItemKey(item, index) {
  return item.itemId || `${item.productId || item.productName || "item"}::${index}`;
}

function itemStatus(item, requestStatus) {
  return item.status || (item.purchased ? "completed" : requestStatus);
}

function aggregateRequestStatus(items, fallback) {
  if (!items.length) return fallback;
  const statuses = items.map((item) => itemStatus(item, fallback));
  if (statuses.some((status) => status === "submitted" || status === "pending")) return "submitted";
  if (statuses.some((status) => status === "approved")) return "approved";
  if (statuses.every((status) => status === "rejected" || status === "supplier_unavailable")) return "rejected";
  if (statuses.every((status) => status === "completed" || status === "fulfilled" || status === "rejected" || status === "supplier_unavailable")) return "completed";
  return fallback;
}

export function updateRequestItem(requestId, itemId, patch) {
  const nowIso = new Date().toISOString();
  let updatedRequest = null;
  const next = loadRequests().map((request) => {
    if (request.id !== requestId) return request;
    const items = (request.items || []).map((item, index) =>
      requestItemKey(item, index) === itemId ? { ...item, itemId, ...patch, updatedAt: nowIso } : item
    );
    updatedRequest = {
      ...request,
      items,
      status: aggregateRequestStatus(items, request.status),
      updatedAt: nowIso,
    };
    return updatedRequest;
  });
  saveRequests(next);
  if (updatedRequest) patchRequestItem(requestId, itemId, patch);
  return next;
}

export function removeRequestItem(requestId, itemId) {
  let removedWholeRequest = false;
  let updatedRequest = null;
  const next = loadRequests().flatMap((request) => {
    if (request.id !== requestId) return [request];
    const items = (request.items || []).filter((item, index) => requestItemKey(item, index) !== itemId);
    if (!items.length) {
      removedWholeRequest = true;
      return [];
    }
    updatedRequest = {
      ...request,
      items,
      status: aggregateRequestStatus(items, request.status),
      updatedAt: new Date().toISOString(),
    };
    return [updatedRequest];
  });
  saveRequests(next);
  if (removedWholeRequest) deleteItem("requests", requestId);
  else if (updatedRequest) deleteRequestItem(requestId, itemId);
  return next;
}

// ---------- Session ----------
// The active tab always gets a sessionStorage entry. On a personal device,
// the user may also opt into a 30-day remembered session. Only the user id
// and expiry are stored; the PIN is never persisted.
export function setSession(userId, remember = false) {
  sessionStorage.setItem(SESSION_KEY, userId || "");
  if (remember && userId) {
    writeJSON(REMEMBERED_SESSION_KEY, {
      userId,
      expiresAt: Date.now() + REMEMBERED_SESSION_DAYS * 24 * 60 * 60 * 1000,
    });
  } else {
    localStorage.removeItem(REMEMBERED_SESSION_KEY);
  }
}
export function getSession() {
  const active = sessionStorage.getItem(SESSION_KEY) || "";
  if (active) return active;
  const remembered = readJSON(REMEMBERED_SESSION_KEY, null);
  if (!remembered?.userId || remembered.expiresAt <= Date.now()) {
    localStorage.removeItem(REMEMBERED_SESSION_KEY);
    return "";
  }
  sessionStorage.setItem(SESSION_KEY, remembered.userId);
  return remembered.userId;
}
export function clearSession() {
  sessionStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(REMEMBERED_SESSION_KEY);
}
export function getCurrentUser() {
  const id = getSession();
  if (!id) return null;
  const user = getUserById(id);
  if (!user || user.active === false) {
    clearSession();
    return null;
  }
  return user;
}

// ---------- Helpers ----------
export function timeAgo(iso) {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function uid(prefix = "id") {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function resizeDataUrl(dataUrl, maxSize = 360, quality = 0.58) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => reject(new Error("Invalid image"));
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxSize / Math.max(width, height));
      width = Math.max(1, Math.round(width * scale));
      height = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Image canvas unavailable"));
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      try {
        resolve(canvas.toDataURL("image/jpeg", quality));
      } catch (e) {
        reject(e);
      }
    };
    img.src = dataUrl;
  });
}

// Product pictures are reference thumbnails, not originals. Keeping them at
// catalog size dramatically reduces localStorage pressure and sync payloads.
export function fileToDataUrl(file, maxSize = 360, quality = 0.58) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = async () => {
      try {
        resolve(await resizeDataUrl(reader.result, maxSize, quality));
      } catch (e) {
        reject(e);
      }
    };
    reader.readAsDataURL(file);
  });
}

// On startup, shrink legacy phone-sized/base64 product photos once they are
// pulled from the backend. Small images and normal https:// URLs are skipped.
// The optimized copies are saved locally and mirrored back to MongoDB so the
// next device receives the lightweight version too.
export async function optimizeStoredProductImages() {
  const products = loadProducts();
  if (!products.length) return { changed: 0, savedBytes: 0 };

  let changed = 0;
  let savedBytes = 0;
  const nowIso = new Date().toISOString();
  const next = [...products];
  const changedProducts = [];

  for (let i = 0; i < products.length; i += 1) {
    const product = products[i];
    const image = product?.image;
    if (typeof image !== "string" || !image.startsWith("data:image/") || image.length < 40000) {
      continue;
    }
    try {
      const optimized = await resizeDataUrl(image);
      if (!optimized || optimized.length >= image.length) continue;
      savedBytes += image.length - optimized.length;
      changed += 1;
      const updated = { ...product, image: optimized, updatedAt: nowIso };
      next[i] = updated;
      changedProducts.push(updated);
    } catch (e) {
      console.warn("[storage] couldn't optimize product image", product?.id, e);
    }
  }

  if (changed > 0) {
    saveProducts(next);
    changedProducts.forEach((product) => pushItem("products", product));
  }

  return { changed, savedBytes };
}

// export re-used role constant for convenience
export { ROLES };
